import React, { useState, useEffect, useCallback } from 'react';
import { listTemplates, deleteTemplate } from '../../utils/financialAnalystApi';
import './FinancialAnalyst.css';

const PERIOD_OPTIONS = (() => {
  const options = [];
  const now = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    options.push({
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      start: d.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    });
  }
  return options;
})();

const FinancialAnalystLanding = ({
  locations,
  selectedHotelId,
  selectedHotelName,
  onHotelChange,
  onSelectTemplate,
  onCreateTemplate,
  onEditTemplate,
  packageHistory,
  onLoadPackage,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  // Load templates only when a property is selected
  const loadTemplates = useCallback(async () => {
    const orgId = localStorage.getItem('currentOrgId');
    if (!orgId || !selectedHotelId) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    try {
      const result = await listTemplates(orgId);
      const all = Array.isArray(result) ? result : (result.templates || result || []);
      // Show system templates (no locationId) + templates matching this location
      const filtered = all.filter(t =>
        !t.locationId || t.locationId === parseInt(selectedHotelId)
      );
      setTemplates(filtered);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
    setLoadingTemplates(false);
  }, [selectedHotelId]);

  useEffect(() => {
    loadTemplates();
    setSelectedTemplate(null);
  }, [loadTemplates]);

  const handleDeleteTemplate = async (e, template) => {
    e.stopPropagation();
    if (!window.confirm(`Delete template "${template.name || template.title}"?`)) return;
    try {
      const orgId = localStorage.getItem('currentOrgId');
      await deleteTemplate(template.id, orgId);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
      if (selectedTemplate?.id === template.id) {
        setSelectedTemplate(null);
      }
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const handleEditTemplate = (e, template) => {
    e.stopPropagation();
    onEditTemplate(template);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate((prev) => (prev?.id === template.id ? null : template));
  };

  const handleGenerate = () => {
    if (!selectedHotelId || !selectedTemplate) return;
    onSelectTemplate(selectedTemplate, PERIOD_OPTIONS[selectedPeriod]);
  };

  const canGenerate = selectedHotelId && selectedTemplate && selectedPeriod !== null;

  // Determine the period type label from the selected template
  const periodTypeLabel = selectedTemplate?.periodType === 'quarterly'
    ? 'Quarter'
    : selectedTemplate?.periodType === 'trailing_12'
      ? 'Trailing 12 End Month'
      : 'Month';

  return (
    <div className="fa-landing">
      {/* ===== Property Selector ===== */}
      <div className="fa-landing-section">
        <label className="fa-form-label">Select Property</label>
        <select
          className="fa-form-select"
          value={selectedHotelId || ''}
          onChange={(e) => onHotelChange(e.target.value)}
        >
          <option value="" disabled>
            Choose a property...
          </option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name}
            </option>
          ))}
        </select>
      </div>

      {/* ===== Template Grid (only after property selected) ===== */}
      {!selectedHotelId && (
        <div className="fa-empty-state" style={{ marginTop: 32 }}>
          Select a property above to generate an owner financial review package.
        </div>
      )}

      {selectedHotelId && <><div className="fa-landing-section">
        <div className="fa-landing-section-title">Choose Package Format</div>

        {loadingTemplates ? (
          <div className="fa-template-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="fa-template-card" style={{ opacity: 0.5 }}>
                <div className="fa-skeleton" style={{ height: 20, width: '70%', marginBottom: 8 }} />
                <div className="fa-skeleton" style={{ height: 14, width: '90%' }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="fa-template-grid">
            {templates.map((template) => {
              const isSystem = template.isSystem || template.is_system;
              const isSelected = selectedTemplate?.id === template.id;
              return (
                <div
                  key={template.id}
                  className={`fa-template-card${isSelected ? ' selected' : ''}`}
                  onClick={() => handleSelectTemplate(template)}
                >
                  <div className="fa-template-card-title">
                    {isSystem && (
                      <span className="fa-status-pill" style={{ fontSize: 10, marginRight: 6, verticalAlign: 'middle' }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 3, opacity: 0.5 }}>
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        System
                      </span>
                    )}
                    {template.name || template.title}
                  </div>
                  <div className="fa-template-card-desc">
                    {template.description || template.periodType?.replace(/_/g, ' ') || 'Custom template'}
                  </div>
                  {!isSystem && (
                    <div className="fa-template-card-actions">
                      <button
                        className="fa-btn fa-btn-sm"
                        onClick={(e) => handleEditTemplate(e, template)}
                        title="Edit template"
                        style={{ padding: '2px 6px', border: 'none', background: 'transparent' }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className="fa-btn fa-btn-sm"
                        onClick={(e) => handleDeleteTemplate(e, template)}
                        title="Delete template"
                        style={{ padding: '2px 6px', border: 'none', background: 'transparent', color: '#ef4444' }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Create New Template card */}
            <div
              className="fa-template-card"
              onClick={onCreateTemplate}
              style={{
                border: '2px dashed var(--line)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                minHeight: 100,
                color: 'var(--muted)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 8, opacity: 0.6 }}>
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Create New Format</span>
            </div>
          </div>
        )}
      </div>

      {/* ===== Period Selector (visible only when template selected) ===== */}
      {selectedTemplate && (
        <div className="fa-landing-section">
          <label className="fa-form-label">{periodTypeLabel}</label>
          <select
            className="fa-form-select"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
          >
            {PERIOD_OPTIONS.map((p, i) => (
              <option key={i} value={i}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ===== Generate Button ===== */}
      <div className="fa-landing-section">
        <button
          className="fa-btn fa-btn-primary"
          disabled={!canGenerate}
          onClick={handleGenerate}
          style={{ width: '100%', justifyContent: 'center', padding: '12px 24px', fontSize: 14 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          Generate Owner Package
        </button>
      </div>

      {/* ===== Recent Packages ===== */}
      {packageHistory && packageHistory.length > 0 && (
        <div className="fa-landing-section">
          <div
            className="fa-landing-section-title"
            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => setShowHistory((prev) => !prev)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{
                transform: showHistory ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s ease',
              }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            Recent Packages ({packageHistory.length})
          </div>

          {showHistory && (
            <div style={{ marginTop: 8 }}>
              {packageHistory.map((pkg) => (
                <div
                  key={pkg.id}
                  className="fa-history-item"
                  onClick={() => onLoadPackage(pkg.id)}
                >
                  <div className="fa-history-item-left">
                    <div className="fa-history-item-title">{pkg.title}</div>
                    <div className="fa-history-item-meta">
                      {pkg.periodStart &&
                        new Date(pkg.periodStart).toLocaleDateString('en-US', {
                          month: 'short',
                          year: 'numeric',
                        })}
                      {pkg.templateType && ` · ${pkg.templateType.replace(/_/g, ' ')}`}
                    </div>
                  </div>
                  <span className={`fa-status-pill ${pkg.status || 'draft'}`}>
                    <span className="fa-status-dot" />
                    {pkg.status?.replace(/_/g, ' ') || 'Draft'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </>}
    </div>
  );
};

export default FinancialAnalystLanding;
